import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import { isoLangs } from '@zedoc/iso-langs';
import { CollapseItem } from '../../../common/components/Collapse';
import Stack from '../../../common/components/primitives/Stack';
import Cluster from '../../../common/components/primitives/Cluster';
import Text from '../../../common/components/base/Text';
import { apiAdminAllOrganisationMessageTemplates } from '../../../common/api/admin';
import { default as OrganisationMessageTemplateSelect } from '../../../common/selectors/OrganisationMessageTemplate';
import MessageTemplateEditor from './MessageTemplateEditor';
import { SECTIONS, templateHeading, sendsSms } from '../sections';
import { messageCompleteness, groupCompleteness } from '../completeness';

/**
 * TECH-2730. The message templates list, as two group accordions over nine
 * messages rather than two flat tables over six.
 *
 * WHY A COUNT PER ROW. The point of the redesign: an administrator can see
 * which messages are missing a language without opening each one. The
 * denominator is the ORGANISATION's configured language set, which is the
 * whole reason authoring languages were decoupled from the provider portal's
 * interface locales — until that change a customer with Arabic- and
 * German-speaking patients could not write to them at all, and nothing on
 * the page said why.
 *
 * WHY THE GAP IS A ROW AND NOT AN INFERENCE. A count alone tells you
 * something is missing but not what. The missing languages are named on the
 * row, so the next action is obvious.
 */

/**
 * The completeness chip.
 *
 * NOT a solid Tag. The brand balance is roughly 70% neutral / 25% blue / 5%
 * signal, and a filled amber block on every row is far past 5% — it read as
 * the loudest thing on a page whose subject is the messages, not the counts.
 * A hairline chip with coloured TEXT carries the same information at a
 * fraction of the weight.
 *
 * Colour is never the only signal: the count itself says 3 / 4, and the row
 * names the missing languages underneath.
 */
const CountChip = ({ authored, total }) => {
  const complete = total > 0 && authored === total;
  return (
    <span
      className={[
        'inline-flex shrink-0 items-center rounded-sm border px-2 py-0.5',
        'font-mono text-xs tracking-wider',
        complete
          ? 'border-success text-success'
          : 'border-divider text-on-surface opacity-80',
      ].join(' ')}
    >
      {`${authored} / ${total}`}
    </span>
  );
};

CountChip.propTypes = {
  authored: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
};

/**
 * TECH-2730 #4 (Tia, QA round 2/3). Language counts on a group header: the
 * patient counts on PEP (the only messages patients receive -- in the top
 * block, above both groups, they read as staff numbers), and the staff
 * counts on Staff. Same chip as the language row. `default` is a staff user
 * with no notification language, who receives English.
 */
const LanguageCounts = ({ languages, counts, testId, patients }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid={testId}>
      {languages.map((code) => {
        const count = (counts || {})[code] || 0;
        const name =
          code === 'default'
            ? t('forms:notificationLanguage.default')
            : (isoLangs[code] && isoLangs[code].name) || code;
        // Hover text on BOTH kinds of chip (QA round 4, issue 5: the staff
        // chips had none, so the two looked alike and behaved differently).
        // Four literal keys, one per branch, for the i18n coverage guard.
        let title;
        if (patients && count === 1) {
          title = t('patientCountOne', {
            name,
            count,
            defaultValue: '{{name}} — {{count}} patient',
          });
        } else if (patients) {
          title = t('patientCountOther', {
            name,
            count,
            defaultValue: '{{name}} — {{count}} patients',
          });
        } else if (count === 1) {
          title = t('staffCountOne', {
            name,
            count,
            defaultValue: '{{name}} — {{count}} staff member',
          });
        } else {
          title = t('staffCountOther', {
            name,
            count,
            defaultValue: '{{name}} — {{count}} staff',
          });
        }
        return (
          <span
            key={code}
            title={title}
            className="inline-flex items-center gap-2 border border-divider bg-surface-500 rounded-sm px-2 py-0.5"
          >
            <span className="font-mono text-xs uppercase tracking-wider">
              {code === 'default' ? name : code}
            </span>
            <span className="text-xs opacity-70">{count}</span>
          </span>
        );
      })}
    </div>
  );
};

LanguageCounts.propTypes = {
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  counts: PropTypes.objectOf(PropTypes.number),
  testId: PropTypes.string.isRequired,
  patients: PropTypes.bool,
};

LanguageCounts.defaultProps = {
  counts: {},
  patients: false,
};

const MessageTemplatesAccordion = ({
  organisationId,
  configuredLanguages,
  canUpdate,
  patientLanguageCounts,
  patientLanguageCountsAt,
  staffLanguageCounts,
}) => {
  // The DEFAULT namespace (common), not 'forms'. Bound to 'forms' every lookup
  // here missed and silently fell through to its defaultValue — the section
  // headings rendered as the raw keys "staff" and "pep", and messagePurpose.*
  // only LOOKED right because PURPOSE_DEFAULTS carries the same English.
  const { t } = useTranslation();

  // Which message is open for editing, inline. Replaces the modal and its
  // two store keys: an editor that hid the rest of the page was the one
  // exception to how everything else on this surface is edited.
  const [editingPurpose, setEditingPurpose] = useState(null);

  const { ready: isReady } = useDDPSubscription(
    organisationId &&
      apiAdminAllOrganisationMessageTemplates.withParams({ organisationId }),
  );

  const allTemplates = useSelector(OrganisationMessageTemplateSelect.all());

  const templatesByPurpose = useMemo(() => {
    const byPurpose = {};
    (allTemplates || [])
      .filter((template) => template.organisationId === organisationId)
      .forEach((template) => {
        byPurpose[template.purpose] = template;
      });
    return byPurpose;
  }, [allTemplates, organisationId]);

  // GATED ON THE TEMPLATE ONLY, NOT ON canUpdate. Darryl's decision,
  // 2026-09-22, resolving the marker that stood here: a view-only admin
  // (MESSAGE_TEMPLATE.search WITHOUT .update) should be able to READ every
  // message, as they could in the EmailTemplatesTable this accordion
  // replaced, which showed name, purpose, languages and subject to everyone
  // who could subscribe and gated only onRow.
  //
  // This is a visibility fix, not a permission change. MessageTemplateEditor
  // already implements the read-only path as `isReadOnly` — Save disabled,
  // Reset to default not rendered, every field read-only — and the server
  // validates ADMIN_UPDATE_MESSAGE_TEMPLATE again on save regardless of what
  // the client offers.
  const handleToggle = useCallback((purpose, template) => {
    if (!template) {
      return;
    }
    setEditingPurpose((current) => (current === purpose ? null : purpose));
  }, []);

  const renderRow = useCallback(
    (purpose) => {
      const template = templatesByPurpose[purpose];
      const completeness = messageCompleteness(template, configuredLanguages);
      const { authored, total, missing } = completeness;
      const isOpen = editingPurpose === purpose;

      const row = (
        <button
          key={`row-${purpose}`}
          type="button"
          data-testid={`message-template-row-${purpose}`}
          onClick={() => handleToggle(purpose, template)}
          disabled={!template}
          className={[
            'w-full text-left border border-divider rounded-md px-4 py-3',
            'transition-colours',
            isOpen
              ? 'bg-surface border-primary ring-1 ring-primary'
              : 'bg-surface hover:bg-surface-500',
            template ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed',
          ].join(' ')}
        >
          <Cluster justify="space-between" wrap="nowrap">
            <div className="min-w-0">
              {/* The organisation's own name for this message when it has
                  set one, otherwise the translated purpose label. See
                  templateHeading for the i18n trade-off that carries. */}
              <div className="font-medium text-on-surface">
                {templateHeading(t, purpose, template)}
              </div>
              <div className="font-mono text-xs tracking-wide opacity-60 mt-0.5">
                {purpose}
              </div>
            </div>
            <Cluster wrap="nowrap">
              <span className="font-mono text-xs uppercase tracking-wider opacity-60">
                {sendsSms(purpose)
                  ? t('messageChannel.emailAndSms', {
                      defaultValue: 'Email · SMS',
                    })
                  : t('messageChannel.email', { defaultValue: 'Email' })}
              </span>
              <CountChip authored={authored} total={total} />
            </Cluster>
          </Cluster>
          {/* Name what is missing, not just how many. A bare count sends the
              author into every message in turn to find out which. */}
          {missing.length > 0 && (
            <div className="mt-2 text-sm opacity-70">
              {t('messageTemplateMissingLanguages', {
                defaultValue: 'Not yet written in {{languages}}',
                languages: missing.map((lng) => lng.toUpperCase()).join(', '),
              })}
            </div>
          )}
        </button>
      );

      if (editingPurpose !== purpose) {
        return row;
      }

      return (
        <Stack key={purpose}>
          {row}
          <MessageTemplateEditor
            template={template}
            configuredLanguages={configuredLanguages}
            canUpdate={canUpdate}
            onClose={() => setEditingPurpose(null)}
          />
        </Stack>
      );
    },
    [
      templatesByPurpose,
      configuredLanguages,
      canUpdate,
      handleToggle,
      editingPurpose,
      t,
    ],
  );

  return (
    <Stack data-testid="message-templates-accordion">
      {!isReady && (
        <Text importance="low">
          {t('loading', { defaultValue: 'Loading…' })}
        </Text>
      )}
      {/* An organisation with no configured languages would otherwise render
          every count as "0 / 0", which reads as broken rather than as
          unconfigured. Say so instead. */}
      {/* GATED ON organisationId, not just isReady. store.js starts with
          organisationId: null and nothing auto-selects, and ddp-connector
          returns { ready: true } for a falsy request (createResourcesSelectorFactory.js
          — "resource was never requested" counts as ready). So on first load
          isReady was true and configuredLanguages was [], and this asserted a
          fact about an organisation nobody had picked. The table this replaced
          guarded the same case with loading={!!organisationId && …}. */}
      {organisationId &&
        isReady &&
        (configuredLanguages || []).length === 0 && (
          <Text importance="low">
            {t('noOrganisationLanguages', {
              defaultValue:
                'This organisation has no message languages configured yet, so nothing can be counted as complete.',
            })}
          </Text>
        )}
      {/* Same gate for the sections: nine disabled rows under two "0 / 0"
          headers before anything is selected is not an empty state, it is a
          statement about nothing. */}
      {organisationId &&
        SECTIONS.map((section) => {
          const group = groupCompleteness(
            section.purposes,
            templatesByPurpose,
            configuredLanguages,
          );
          return (
            <div
              key={section.key}
              className="border border-divider rounded-md bg-surface p-4"
            >
              {/* A plain flex row below, not Cluster: CollapseItem puts `title`
              inside its own Text wrapper and then its own Cluster, and that
              nesting stopped space-between from stretching, so the count badge
              sat against the title rather than at the right edge. And `.title`
              rather than the bare key: emailTemplateSections.<key> is an object
              of title plus description, so asking for the key returned an
              object, fell back to the raw section name, and never showed the
              description at all. */}
              {/* Both groups start CLOSED. Nine messages expanded on load pushed
              everything below the fold and made the page read as a wall; the
              point of the grouped view is that the counts answer "is anything
              missing" without opening anything. */}
              <CollapseItem
                defaultOpen={false}
                // A stable id of our own. CollapseItem used to derive its
                // data-testid from `title`, and this title is a JSX element, so
                // both headers rendered as collapse-button-[object Object] and
                // any getByTestId for a section header matched twice.
                testId={`section-${section.key}`}
                title={
                  <div className="flex w-full items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium">
                        {t(`emailTemplateSections.${section.key}.title`, {
                          defaultValue: section.key,
                        })}
                      </div>
                      <div className="text-sm opacity-70 mt-0.5">
                        {t(`emailTemplateSections.${section.key}.description`, {
                          defaultValue: '',
                        })}
                      </div>
                      {section.key === 'pep' && patientLanguageCountsAt && (
                        <LanguageCounts
                          testId="pep-patient-language-counts"
                          languages={configuredLanguages || []}
                          counts={patientLanguageCounts}
                          patients
                        />
                      )}
                      {section.key === 'staff' && staffLanguageCounts && (
                        <LanguageCounts
                          testId="staff-language-counts"
                          // Default first, then the configured languages,
                          // then any language a user holds that is no longer
                          // configured -- still counted, so it is visible.
                          languages={[
                            'default',
                            ...new Set([
                              ...(configuredLanguages || []),
                              ...Object.keys(staffLanguageCounts).filter(
                                (code) => code !== 'default',
                              ),
                            ]),
                          ]}
                          counts={staffLanguageCounts}
                        />
                      )}
                    </div>
                    <CountChip authored={group.authored} total={group.total} />
                  </div>
                }
                content={
                  <div className="stack-3 pt-3">
                    {section.purposes.map(renderRow)}
                  </div>
                }
              />
            </div>
          );
        })}
    </Stack>
  );
};

MessageTemplatesAccordion.propTypes = {
  organisationId: PropTypes.string,
  configuredLanguages: PropTypes.arrayOf(PropTypes.string),
  canUpdate: PropTypes.bool,
  patientLanguageCounts: PropTypes.objectOf(PropTypes.number),
  patientLanguageCountsAt: PropTypes.instanceOf(Date),
  staffLanguageCounts: PropTypes.objectOf(PropTypes.number),
};

MessageTemplatesAccordion.defaultProps = {
  organisationId: null,
  configuredLanguages: [],
  canUpdate: false,
  patientLanguageCounts: {},
  patientLanguageCountsAt: null,
  staffLanguageCounts: null,
};

export default MessageTemplatesAccordion;
